/**
 * PlanClassify — pure plan classification, attribute-derivation, and display-labeling helpers.
 *
 * Extracted verbatim from script.js (god-object refactor — step 2). All functions read only the
 * plan object (no DOM, no app state); intra-module calls only. Bundled and exposed as
 * window.WoxomQuote.PlanClassify; script.js aliases each back to its local name so every call site
 * — and the implicit window.* globals the e2e relies on (getProductType/navTypeOf/
 * carrierDisplayLabel/coinsuranceChipLabel/getNetworkType) — keeps working unchanged.
 *
 * NOTE: the PRODUCT_TYPES / NETWORK_TYPES data tables stay in script.js (they drive the sidebar
 * nav + the network filter dropdown, not these classifiers).
 */

// Nav/filter-only grouping: Hospital Indemnity plans live under the "Limited Medical" sidebar
// section, and Specified Disease plans under "CI / Specified Disease" (critical-illness). This does
// NOT change getProductType, plan cards, or cart-conflict rules — each stays its own product type
// everywhere else.
const NAV_TYPE_ALIASES = { 'hospital-indemnity': 'limited-medical', 'specified-disease': 'critical-illness' };
export function navTypeOf(productType) { return NAV_TYPE_ALIASES[productType] || productType; }

export function getProductType(plan) {
  var category = (plan.category || '').toLowerCase();
  var type = (plan.type || '').toLowerCase();
  var name = (plan.name || '').toLowerCase();
  var carrier = (plan.carrier || '').toLowerCase();
  var planType = (plan.planType || '').toLowerCase();

  // Additional Plans: My Life, HealthiestYou, New Benefits, Mental Health Complete Plus
  if (name.includes('my life') || carrier.includes('my life')) return 'additional';
  if (name.includes('healthiestyou') || carrier.includes('healthiestyou') ||
      name.includes('healthiest you') || carrier.includes('healthiest you')) return 'additional';
  if (name.includes('new benefits') || carrier.includes('new benefits') ||
      category.includes('new benefits')) return 'additional';
  if (name.includes('mental health complete') || category.includes('mental health complete')) return 'additional';

  if (type === 'aca' || category.includes('aca')) return 'aca';
  // Pivot Health "Latitude" is an accident product (owner 2026-09-25) — checked before the Pivot
  // rule below, which would otherwise pull it into Short-Term Medical.
  if (type === 'latitude' || category.includes('latitude') || name.includes('latitude')) return 'accident';
  // Pivot Health / Pan-American Life medical lines (Quantum, Epic, Economy, Bridge to Medicare,
  // Standard/Deluxe/Core/Choice, Sure Care…) are sold as Short-Term Medical (owner 2026-09-25).
  // Their "Pivot <LINE> Plans" categories carry no STM keyword, so they fell through to the
  // major-medical default — or to term-life, since "Pan-American Life" contains "life".
  // Pivot's standalone dental/vision lines keep their own sections.
  if (isPivotOrPanAmerican(carrier, category, name) &&
      !category.includes('dental') && !category.includes('vision')) return 'stm';
  // Short-Term Medical now covers all term lengths (12/24/36) — term length is a card axis,
  // not a separate product type. UHOne long-term + Allstate multi-term fold into 'stm'.
  if (type === 'long-term' || type === 'tri-term') return 'stm';
  // Short-Term Medical: UHOne SHORT TERM productType and other STM plans
  if (category.includes('short-term') || category.includes('stm') || type === 'stm' || type === 'short-term') return 'stm';
  if (name.includes('short term medical value') || name.includes('short term medical plus') ||
      name.includes('short term medical copay')) return 'stm';
  if (category.includes('gap') || category.includes('expense shield')) return 'gap';
  // Dental — incl. Manhattan Life "DVH" / "DVH Select".
  if (category.includes('dental') || category.includes('dvh') || type.includes('dvh')) return 'dental';
  if (category.includes('vision')) return 'vision';
  if (category.includes('hospital') || category.includes('indemnity')) return 'hospital-indemnity';
  // Accident — incl. Manhattan Life "Affordable Choice".
  if (category.includes('accident') || category.includes('affordable choice') || type.includes('affordable-choice')) return 'accident';
  if (category.includes('critical illness') || category.includes('critical-illness')) return 'critical-illness';
  // Specified disease — incl. Manhattan Life "CHAS".
  if (category.includes('cancer') || category.includes('specified disease') || category.includes('chas') || type.includes('chas')) return 'specified-disease';
  // LifeX: classify by explicit type, not the "life" brand keyword below.
  if (type === 'value_limited') return 'limited-medical';
  if (type === 'major_medical') return 'major-medical';
  // Genuine term life only — exclude LifeX-branded plans (caught above or by the major-medical fallback).
  if ((category.includes('life') || name.includes('life')) && !category.includes('lifex') && !name.includes('lifex')) return 'term-life';
  if (category.includes('wellness') || category.includes('preventive')) return 'wellness';
  // Limited Medical (e.g. PSM MaxGuard, NGAH LimitedMed/LimitedMedU65) — limited-benefit, NOT true major medical.
  if (planType.includes('limited') || category.includes('maxguard') || category.includes('limited medical')) return 'limited-medical';
  if (category.includes('major medical') || category.includes('health')) return 'major-medical';

  return 'major-medical';  // Default fallback
}

function isPivotOrPanAmerican(carrier, category, name) {
  var haystack = carrier + ' ' + category + ' ' + name;
  return haystack.includes('pivot') || /pan[\s-]?american/.test(haystack);
}

export function coinsuranceChipLabel(c) {
  var s = String(c == null ? '' : c);
  var trimmed = s.replace(/\s*to\s*\$[0-9,].*$/i, '').trim();
  return trimmed || s;
}

export function getNetworkType(plan) {
  var network = (plan.network || '').toUpperCase();
  var type = (plan.type || '').toUpperCase();
  var name = (plan.name || '').toUpperCase();

  var combined = network + ' ' + type + ' ' + name;

  if (combined.includes('HMO')) return 'hmo';
  if (combined.includes('PPO')) return 'ppo';
  if (combined.includes('EPO')) return 'epo';
  if (combined.includes('POS')) return 'pos';
  if (combined.includes('INDEMNITY')) return 'indemnity';

  var productType = getProductType(plan);
  if (['accident', 'critical-illness', 'specified-disease', 'hospital-indemnity'].includes(productType)) {
    return 'indemnity';
  }

  return 'ppo';  // Default
}

export function getBenefitLevel(plan) {
  var metalLevel = (plan.metalLevel || '').toLowerCase();
  if (metalLevel === 'catastrophic') return 1;
  if (metalLevel === 'bronze') return 2;
  if (metalLevel === 'silver') return 3;
  if (metalLevel === 'gold') return 4;
  if (metalLevel === 'platinum') return 5;

  var name = (plan.name || '').toLowerCase();
  var category = (plan.category || '').toLowerCase();
  var combined = name + ' ' + category;

  if (combined.includes('platinum') || combined.includes('premier') || combined.includes('elite')) return 5;
  if (combined.includes('gold') || combined.includes('enhanced') || combined.includes('plus')) return 4;
  if (combined.includes('silver') || combined.includes('standard')) return 3;
  if (combined.includes('bronze') || combined.includes('value') || combined.includes('economy')) return 2;
  if (combined.includes('basic') || combined.includes('catastrophic') || combined.includes('minimal')) return 1;

  var deductible = plan.deductible || 0;
  if (deductible === 0) return 5;
  if (deductible <= 1500) return 4;
  if (deductible <= 4000) return 3;
  if (deductible <= 7500) return 2;
  return 1;
}

export function getOopMax(plan) {
  if (plan.planDetails?.moops) {
    var individualMoop = plan.planDetails.moops.find(function(m) {
      return m.type === 'Individual Medical' || m.type === 'Maximum Out of Pocket for Medical and Drug EHB Benefits (Total)';
    });
    if (individualMoop) return individualMoop.amount || 0;
  }

  if (plan.oopMax != null) return plan.oopMax;
  if (plan.outOfPocketMax != null) return plan.outOfPocketMax;
  if (plan.maxOutOfPocket != null) return plan.maxOutOfPocket;

  var deductible = plan.deductible || 0;
  return Math.max(deductible * 2, 5000);
}

export function isHsaEligible(plan) {
  if (plan.planDetails?.hsa_eligible != null) return plan.planDetails.hsa_eligible;
  if (plan.hsaEligible != null) return plan.hsaEligible;
  if (plan.hsa_eligible != null) return plan.hsa_eligible;

  var deductible = plan.deductible || 0;
  var productType = getProductType(plan);

  if (!['aca', 'stm', 'major-medical'].includes(productType)) return false;
  return deductible >= 1600;
}

export function getTermLengthId(plan) {
  var type = (plan.type || '').toLowerCase();
  var details = plan.planDetails || {};

  // Allstate consecutive-terms product (2 × 12-month policies, spec 2026-07-28) — checked
  // before the renewal branch because the synthesized plan also carries stmRenewalTerms: 0.
  if (details.stmConsecutiveTerms === 2) return '2x12';

  // Allstate STM plans with renewal info. stmTermMonths is the actually-quoted term — capped
  // states (TX/NC=4, VA=3, MI=6, SC=11) quote below 12, so renewals multiply the real term.
  if (details.stmRenewalTerms !== undefined) {
    var renewals = details.stmRenewalTerms || 0;
    var termMonths = Number(details.stmTermMonths) || 12;
    return String(termMonths * (renewals + 1));
  }

  // UHOne (short-term / long-term): term comes from the Rate Engine's coverageMonthsStart/End,
  // stamped planDetails.termMonths/termMonthsMin in uhone-api.js (TX/NC quote 1–4 months,
  // FL etc. 12; TriTerm rows carry 36 = three consecutive terms). Never assume 12.
  if (type === 'short-term' || type === 'long-term') {
    return Number(details.termMonths) > 0 ? String(Number(details.termMonths)) : 'na';
  }

  // ACA plans are annual
  if (type === 'aca') return '12';

  // Other plan types
  return 'na';
}

// Human label for the policy term meta chip (moved from script.js so capped-state terms stay in
// one place with getTermLengthId). stmTermMonths = actually-quoted STM term; absent → legacy 12.
export function getPolicyTermLabel(plan) {
  var type = (plan.type || '').toLowerCase();
  var details = plan.planDetails || {};

  // Allstate consecutive-terms product (spec 2026-07-28) — checked before the renewal
  // branch because the synthesized plan also carries stmRenewalTerms: 0.
  if (details.stmConsecutiveTerms === 2) return '24 months (2 × 12-month terms)';

  // Allstate STM plans with renewal info
  if (details.stmRenewalTerms !== undefined) {
    var renewals = details.stmRenewalTerms || 0;
    var rateLock = (renewals > 0 && details.stmRenewalRateLock) ? ' fixed' : '';
    var months = (Number(details.stmTermMonths) || 12) * (renewals + 1);
    return months + ' months' + rateLock;
  }

  // UHOne (short-term / long-term): same termMonths/termMonthsMin data as getTermLengthId. A
  // capped state quotes a real 1–N month range (termMonthsMin < termMonths) — "up to N months" —
  // while a flat term (FL's 12, TriTerm's 36) reads as a plain "N months". Never assume 12.
  if (type === 'short-term' || type === 'long-term') {
    var termMonths = Number(details.termMonths) || 0;
    var termMonthsMin = Number(details.termMonthsMin) || 0;
    if (termMonths > 0 && termMonthsMin > 0 && termMonthsMin < termMonths) return 'up to ' + termMonths + ' months';
    if (termMonths > 0) return termMonths + ' months';
    return 'N/A';
  }

  // ACA plans are annual
  if (type === 'aca') return '12 months';

  return 'N/A';
}

// Carrier display-name normalization (long/legacy API names → short brand names). Exact map for
// stable names; PATTERN rules for carriers whose raw API strings vary a lot.
const CARRIER_DISPLAY_NAMES = {
  'allstate health': 'Allstate Health', // carrier-requested label (Kevin, 2026-08-10)
  'amerihealth caritas next': 'AmeriHealth',
  'molina healthcare': 'Molina',
  'oscar health maintenance organization of florida': 'Oscar',
  'phcs': 'LifeX',
};
function carrierDisplayName(raw) {
  var s = String(raw || '').trim();
  var key = s.toLowerCase();
  // Pattern rules (order matters — HMO before the generic Florida Blue):
  if (/^new benefits\b/.test(key)) return 'UHOne';                 // New Benefits, Ltd is a UHOne product (#1)
  if (/^cigna\b/.test(key)) return 'Cigna';                        // Cigna / Cigna Healthcare of Florida, Inc → one "Cigna" (#2)
  if (/^florida blue\s+hmo\b/.test(key)) return 'Florida Blue HMO'; // drop "(a BlueCross BlueShield FL Company)" (#3)
  if (/^florida blue\b/.test(key)) return 'Florida Blue';
  if (/^ambetter\b/.test(key)) return 'Ambetter';
  return CARRIER_DISPLAY_NAMES[key] || s;
}

// Display-only carrier label. LifeX plans carry network "PHCS" and no carrier; show the brand
// "LifeX" instead of the network. Never mutate plan data.
export function carrierDisplayLabel(plan) {
  if (plan.carrier) return carrierDisplayName(plan.carrier);
  if ((plan.category || '').toLowerCase().indexOf('lifex') !== -1) return 'LifeX';
  return carrierDisplayName(plan.network || 'LifeX');
}

export const PlanClassify = {
  navTypeOf, getProductType, coinsuranceChipLabel, getNetworkType, getBenefitLevel,
  getOopMax, isHsaEligible, getTermLengthId, getPolicyTermLabel, carrierDisplayName, carrierDisplayLabel,
};
